import { useCallback, useMemo } from "react";

import { useDispatch, useSelector } from "react-redux";

import { useTranslate } from "@similarweb/i18n-provider";

import { useStorage } from "app/custom-hooks/storage/useStorage";
import type {
    ColumnListPlugin,
    ColumnPickerPluginConfig,
    ColumnPlugin,
    HeaderPlugin,
} from "app/pages/sales-intelligence/common-components/column-picker";
import { appsColumnPickerPlugins } from "app/pages/sales-intelligence/modules/apps-search/plugins/column-picker";
import { appsSearchSessionStorage } from "app/pages/sales-intelligence/modules/apps-search/services/storage";
import { dataCreditsColumnPickerPlugins } from "app/pages/sales-intelligence/modules/quota/plugins/column-picker";
import { useDataCreditsQuery } from "app/pages/sales-intelligence/modules/quota/queries/useDataCreditsQuery";
import { useIsIndustryTaxonomyOptedIn } from "app/pages/sales-intelligence/modules/industry/hooks/useIsIndustryTaxonomyOptedIn";
import {
    selectIsDataCostVisible,
    setDataCostVisibility,
    toggleDataCostVisibility,
} from "app/pages/sales-intelligence/modules/quota/store";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";

import {
    adIntelligenceColumnPickerPlugins,
    conversionRateColumnPickerPlugins,
    legacyIndustryColumnPickerPlugins,
} from "../plugins/column-picker";
import { leadsSearchSessionStorage } from "../services/storage";
import { leadsCostTypeMapper } from "../utils/quota";

type HookValue = {
    config: ColumnPickerPluginConfig | null;
    pluginCleanup: VoidFunction;
};

export const useLeadsColumnSelectionPlugins = (): HookValue => {
    const dispatch = useDispatch();
    const { translate } = useTranslate();

    const isDataCostVisible = useSelector(selectIsDataCostVisible);

    const { data, isError } = useDataCreditsQuery({
        select: ({ dataPoints }) => dataPoints,
    });

    const [isAdIntelligenceHookSubmitted, setIsAdIntelligenceHookSubmitted] = useStorage(
        leadsSearchSessionStorage.adIntelligenceColumnHookSubmitted,
    );

    const [isAppsColumnHookSubmitted, setIsAppsColumnHookSubmitted] = useStorage(
        appsSearchSessionStorage.appsFeatureRequestSent,
    );

    const [isConversionRateHookSubmitted, setIsConversionRateHookSubmitted] = useStorage<boolean>(
        leadsSearchSessionStorage.conversionRateColumnHookSubmitted,
    );

    const isIndustryTaxonomyOptedIn = useIsIndustryTaxonomyOptedIn();

    const cleanup = useCallback(() => {
        dispatch(setDataCostVisibility(false));
    }, [dispatch]);

    const config = useMemo<ColumnPickerPluginConfig | null>(() => {
        const columnPlugins: ColumnPlugin[] = [];

        if (isIndustryTaxonomyOptedIn) {
            columnPlugins.push(legacyIndustryColumnPickerPlugins.createLegacyBadge());
        }

        const headerPlugins: HeaderPlugin[] = [];
        const listPlugins: ColumnListPlugin[] = [];

        if (!salesSettingsHelper.isConsolidatedAppsEnabled) {
            columnPlugins.push(
                appsColumnPickerPlugins.createUpgradeButton({
                    isHookSubmitted: isAppsColumnHookSubmitted,
                    onHookSubmit: () => setIsAppsColumnHookSubmitted(true),
                }),
            );
        }

        if (salesSettingsHelper.isDataCreditsEnabled && !isError && data) {
            columnPlugins.push(
                dataCreditsColumnPickerPlugins.createCostBadge({
                    dataPoints: data,
                    isCostVisible: isDataCostVisible,
                    costTypeMapper: leadsCostTypeMapper,
                }),
            );
            headerPlugins.push(
                dataCreditsColumnPickerPlugins.createCostToggle({
                    isChecked: isDataCostVisible,
                    onToggle: () => dispatch(toggleDataCostVisibility()),
                }),
            );
            listPlugins.push(
                dataCreditsColumnPickerPlugins.createTotalCostBadge({
                    isCostVisible: isDataCostVisible,
                    dataPoints: data,
                    translate,
                    costTypeMapper: leadsCostTypeMapper,
                }),
            );
        }

        if (!salesSettingsHelper.isSIAdIntelligenceEnabled) {
            columnPlugins.push(
                adIntelligenceColumnPickerPlugins.createUpgradeButton({
                    isHookSubmitted: isAdIntelligenceHookSubmitted,
                    onHookSubmit: () => setIsAdIntelligenceHookSubmitted(true),
                }),
            );
        }

        if (!salesSettingsHelper.isEcommConversionRateEnabled) {
            columnPlugins.push(
                conversionRateColumnPickerPlugins.createUpgradeButton({
                    isHookSubmitted: isConversionRateHookSubmitted,
                    onHookSubmit: () => setIsConversionRateHookSubmitted(true),
                }),
            );
        }

        if (columnPlugins.length === 0 && headerPlugins.length === 0 && listPlugins.length === 0) {
            return null;
        }

        return { columnPlugins, headerPlugins, listPlugins };
    }, [
        isError,
        data,
        isAppsColumnHookSubmitted,
        setIsAppsColumnHookSubmitted,
        isDataCostVisible,
        dispatch,
        isAdIntelligenceHookSubmitted,
        setIsAdIntelligenceHookSubmitted,
        isConversionRateHookSubmitted,
        setIsConversionRateHookSubmitted,
        isIndustryTaxonomyOptedIn,
        translate,
    ]);

    return { config, pluginCleanup: cleanup };
};
